import { React, useState, useEffect } from 'react';

import { FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatIsoDate, formatMonthYearLabelPtBr } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/fireExtinguisherUpdate.css';

const AtualizarExtintores = () => {
    const [dados, setDados] = useState([]);
    const [site, setSite] = useState('');
    const [listaSites, setListaSites] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [listaUsinas, setListaUsinas] = useState([]);
    const [fornecedor, setFornecedor] = useState('');
    const [tipo, setTipo] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [obs, setObs] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        async function buscarUsinas () {
            const response = await fetchFlask('buscarUsinas');
            if (response.status !== 'error') {
                const dadosFetch = response.dados;
                setDados(dadosFetch);
                const dadosSite = [...new Set(dadosFetch.map(item => item[0]))];
                setListaSites(dadosSite);
            } else {
                handleAlerta(response);
            }
        };
        buscarUsinas();
    }, []);

    function siteSelect (e) {
        setSite(e.target.value);
        setUsinas([]);

        const dadosFiltrados = dados.filter(item => item[0] === e.target.value);
        const dadosUsinas = [...new Set(dadosFiltrados.map(item => item[1]))];
        dadosUsinas.sort();
        dadosUsinas.unshift('Selecionar Todas');
        setListaUsinas(dadosUsinas);
        setUsinas([]);
    };

    function usinasChange (e, values) {
        if (values.includes('Selecionar Todas') && !usinas.includes('Selecionar Todas')) {
            let todasUsinas = [];
            todasUsinas = todasUsinas.concat(listaUsinas);
            todasUsinas.splice(0, 1);
            setUsinas(todasUsinas);
        } else {
            setUsinas(values);
        }
    };

    async function submitAtualizarExtintores (e) {
        e.preventDefault();
        setIsSubmit(true);

        if (!dateTime) {
            setError(true);
            setIsSubmit(false);
            return;
        }
        setError(false);
        const validade = formatIsoDate(dateTime);

        const body = JSON.stringify({
            usinas,
            fornecedor,
            tipo,
            validade,
            obs
        });
        try {
            const response = await fetchFlask('atualizarExtintores', body);
            handleAlerta(response);
            if (response.status === 'success') {
                setSite('');
                setUsinas([]);
                setFornecedor('');
                setTipo('');
                setDateTime(null);
                setObs('');
            }
        } finally {
            setIsSubmit(false);
        }
    };

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const validadeLabel = formatMonthYearLabelPtBr(dateTime, 'Pendente');
    const usinasLabel = usinas.length > 0 ? `${usinas.length} selecionada${usinas.length > 1 ? 's' : ''}` : 'Nenhuma selecionada';
    const completedFields = [
        site,
        usinas.length,
        fornecedor,
        tipo,
        dateTime
    ].filter(Boolean).length;

    return (
        <Box className='mainBox extinguisher-update-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Atualizar Extintores</PageHeader>

            <form method='POST' id='formAtualizarExtintores' onSubmit={submitAtualizarExtintores} className='extinguisher-update-layout'>
                <Box className='extinguisher-update-workspace'>
                    <Box className='extinguisher-update-intro'>
                        <Box>
                            <h2>Controle de validade dos extintores</h2>
                        </Box>
                        <p>
                            Atualize fornecedor, tipo e competência de validade dos extintores vinculados às usinas selecionadas.
                        </p>
                    </Box>

                    <Box className='extinguisher-update-panel'>
                        <Box className='extinguisher-update-section-heading'>
                            <ApartmentRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Site e usinas</strong>
                            </Box>
                        </Box>

                        <Box className='extinguisher-update-fields-grid'>
                            <FormControl required className='extinguisher-update-select-control'>
                                <InputLabel id='labelSite'>Site</InputLabel>
                                <Select
                                    id='selectSite'
                                    labelId='labelSite'
                                    label='Site'
                                    value={site}
                                    onChange={siteSelect}
                                >
                                    {listaSites.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Autocomplete
                                className='extinguisher-update-autocomplete'
                                multiple
                                disableCloseOnSelect
                                id='usinas'
                                limitTags={4}
                                value={usinas}
                                options={listaUsinas}
                                onChange={usinasChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Usinas'
                                        required={usinas.length === 0}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box className='extinguisher-update-panel'>
                        <Box className='extinguisher-update-section-heading'>
                            <LocalFireDepartmentRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Dados do extintor</strong>
                            </Box>
                        </Box>

                        <Box className='extinguisher-update-fields-grid'>
                            <TextField
                                required
                                label='Fornecedor'
                                value={fornecedor}
                                onChange={(e) => setFornecedor(e.target.value)}
                            />
                            <TextField
                                required
                                label='Tipo'
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                            />
                            <FormControl className='extinguisher-update-date-control' error={error}>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <DatePicker id='data' label='Validade'
                                        views={['month', 'year']}
                                        openTo='month'
                                        minDate={new Date('2023-01-02')}
                                        value={dateTime}
                                        onChange={(value) => setDateTime(value)}
                                        slotProps={{
                                            openPickerButton: {
                                                color: 'primary'
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {error && (
                                    <FormHelperText>Por favor selecione uma validade.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='extinguisher-update-panel'>
                        <Box className='extinguisher-update-section-heading'>
                            <NoteAddRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Observação operacional</strong>
                            </Box>
                        </Box>

                        <TextField
                            label='Observação'
                            id='obs'
                            multiline
                            minRows={3}
                            value={obs}
                            onChange={(e) => setObs(e.target.value)}
                        />
                    </Box>
                </Box>

                <Box className='extinguisher-update-summary'>
                    <Box className='extinguisher-update-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da atualização</strong>
                        </Box>
                    </Box>

                    <Box className='extinguisher-update-summary-list'>
                        <Box>
                            <span>Site</span>
                            <strong className={!site ? 'is-empty' : undefined}>{site || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Usinas</span>
                            <strong className={usinas.length === 0 ? 'is-empty' : undefined}>{usinasLabel}</strong>
                        </Box>
                        <Box>
                            <span>Fornecedor</span>
                            <strong className={!fornecedor ? 'is-empty' : undefined}>{fornecedor || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Tipo</span>
                            <strong className={!tipo ? 'is-empty' : undefined}>{tipo || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Validade</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{validadeLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='extinguisher-update-attention'>
                        <LocalFireDepartmentRoundedIcon />
                        <p>
                            A atualização será aplicada às usinas selecionadas. Confira o site antes de enviar.
                        </p>
                    </Box>

                    <Box className='extinguisher-update-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/5 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Atualizar Extintores
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AtualizarExtintores;
